/** Copyright (c) 2019 T.Rowe Price. All rights reserved. */

import first from 'lodash/first';
import isEmpty from 'lodash/isEmpty';
import isNull from 'lodash/isNull';
import { createSelector } from 'reselect';

import {
  DAYTIME,
  EVENING,
  MOBILE,
  ANNUAL_VERIFICATION,
  ANNUAL_VERIFICATION_TITLE,
  CONTACT_INFORMATION_UPDATE_TITLE,
  EMAIL_ADDRESSES_TITLE,
  INVALID_EMAIL_ADDRESSES_ONLY,
  INVALID_MAILING_ADDRESSES_ONLY,
  INVALID_MAILING_AND_EMAIL_ADDRESSES,
  INVALID_MAILING_AND_NO_EMAIL_ADDRESSES,
  MAILING_ADDRESSES_TITLE,
  NO_EMAIL_ADDRESSES_ONLY,
  NO_EMAIL,
  EMAIL_NOT_DELIVERABLE,
  PRIMARY_CONTACT,
  VERIFICATION_INVALID_EMAIL_ADDRESSES,
  VERIFICATION_INVALID_MAILING_ADDRESSES,
  VERIFICATION_INVALID_MAILING_AND_EMAIL_ADDRESSES,
  VERIFICATION_INVALID_MAILING_AND_NO_EMAIL_ADDRESSES,
  VERIFICATION_NO_EMAIL_ADDRESSES,
  RESIDENTIAL_ADDRESS_CONTACT
} from 'services/ContactMethodMaintenance';
import { getPrimaryEmailAddress } from 'state/customer/CustomerSelectors';
import { isContactMethodAnnualVerificationEnabled, residentialAddressEnabled } from 'services/FeatureFlags';
import { getHasReceivedCMMVerification } from 'state/requests/RequestStatusSelectors';

export const getContactMethodMaintenance = (state) =>
  state.contactMethodMaintenance;

export const getPrimaryMailingAddress = createSelector(
  getContactMethodMaintenance,
  (contactMethodMaintenance) => {
    const { mailingAddresses = [] } = contactMethodMaintenance;
    return (
      mailingAddresses !== null &&
      first(
        mailingAddresses.filter(
          (address) =>
            address.typeId === PRIMARY_CONTACT && isNull(address.expirationDate)
        )
      )
    );
  }
);

export const getResidentialAddress = createSelector(
  getContactMethodMaintenance,
  (contactMethodMaintenance) => {
    const { residentialAddresses = [] } = contactMethodMaintenance;
    return (
      residentialAddresses !== null &&
      first(
        residentialAddresses.filter(
          (address) =>
            address.typeId === RESIDENTIAL_ADDRESS_CONTACT && isNull(address.expirationDate)
        )
      )
    );
  }
);

export const getCMMEmailAddress = createSelector(
  getContactMethodMaintenance,
  (contactMethodMaintenance) => {
    const { emailAddresses = [] } = contactMethodMaintenance;
    return (
      emailAddresses !== null &&
      first(
        emailAddresses.filter(
          (email) =>
            email.typeId === PRIMARY_CONTACT && isNull(email.expirationDate)
        )
      )
    );
  }
);

export const getPhoneNumbers = createSelector(
  getContactMethodMaintenance,
  (contactMethodMaintenance) => {
    const { phoneNumbers = [] } = contactMethodMaintenance;
    return (
      phoneNumbers !== null &&
      phoneNumbers.filter((phoneNumber) => isNull(phoneNumber.expirationDate))
    );
  }
);

export const getDaytimePhoneNumber = createSelector(
  getPhoneNumbers,
  (phoneNumbers = []) =>
    phoneNumbers !== null &&
    first(phoneNumbers.filter((phoneNumber) => phoneNumber.typeId === DAYTIME))
);

export const getEveningPhoneNumber = createSelector(
  getPhoneNumbers,
  (phoneNumbers = []) =>
    phoneNumbers !== null &&
    first(phoneNumbers.filter((phoneNumber) => phoneNumber.typeId === EVENING))
);

export const getMobilePhoneNumber = createSelector(
  getPhoneNumbers,
  (phoneNumbers = []) =>
    phoneNumbers !== null &&
    first(phoneNumbers.filter((phoneNumber) => phoneNumber.typeId === MOBILE))
);

export const getFilteredContactMethodMaintenance = createSelector(
  getContactMethodMaintenance,
  getPrimaryMailingAddress,
  getResidentialAddress,
  getCMMEmailAddress,
  getPhoneNumbers,
  (
    contactMethodMaintenance,
    mailingAddresses = {},
    residentialAddresses = {},
    emailAddresses = {},
    phoneNumbers = []
  ) => {
    if(residentialAddressEnabled()) {
      if(residentialAddresses) {
        return {
          ...contactMethodMaintenance,
          mailingAddresses,
          residentialAddresses,
          emailAddresses,
          phoneNumbers
        };
      }
      return {
        ...contactMethodMaintenance,
        mailingAddresses,
        emailAddresses,
        phoneNumbers
      };
    }
    return {
      ...contactMethodMaintenance,
      mailingAddresses,
      emailAddresses,
      phoneNumbers
    };
  }
);

export const getHasDataForContactMethodMaintenanceModal = createSelector(
  getFilteredContactMethodMaintenance,
  (contactMethodMaintenance) =>{

    const hasNonEmptyData = Object.values(contactMethodMaintenance).some((value) => !isEmpty(value));

    if(isContactMethodAnnualVerificationEnabled()) {
      return hasNonEmptyData;
    }

    return hasNonEmptyData
      && !( contactMethodMaintenance.verificationIds?.length === 1 &&
        contactMethodMaintenance.verificationIds?.every((id) => ANNUAL_VERIFICATION === id) );
    }
);

export const getHasContactMethodMaintenanceVerificationIds = createSelector(
  getFilteredContactMethodMaintenance,
  (contactMethodMaintenance) =>
    !isEmpty(contactMethodMaintenance.verificationIds)
);

export const getHasAnnualVerification = createSelector(
  getContactMethodMaintenance,
  (contactMethodMaintenance) => {
    const { verificationIds = [] } = contactMethodMaintenance;
    return isContactMethodAnnualVerificationEnabled()
    && (
      verificationIds !== null &&
      verificationIds.length > 0 &&
      verificationIds.includes(ANNUAL_VERIFICATION)
    );
  }
);

export const getHasNoEmailAddressesOnly = createSelector(
  getFilteredContactMethodMaintenance,
  (contactMethodMaintenance) => {
    const { verificationIds = [] } = contactMethodMaintenance;
    return (
      verificationIds !== null &&
      verificationIds.length === NO_EMAIL_ADDRESSES_ONLY.length &&
      verificationIds.every((id) => NO_EMAIL_ADDRESSES_ONLY.includes(id))
    );
  }
);

export const getHasInvalidEmailAddressesOnly = createSelector(
  getFilteredContactMethodMaintenance,
  (contactMethodMaintenance) => {
    const { verificationIds = [] } = contactMethodMaintenance;
    return (
      verificationIds !== null &&
      verificationIds.length === INVALID_EMAIL_ADDRESSES_ONLY.length &&
      verificationIds.every((id) => INVALID_EMAIL_ADDRESSES_ONLY.includes(id))
    );
  }
);

export const getHasNoEmailAddress = createSelector(
  getFilteredContactMethodMaintenance,
  getPrimaryEmailAddress,
  (contactMethodMaintenance, email) => {
    const { verificationIds = [] } = contactMethodMaintenance;
    return (
      (verificationIds !== null &&
        verificationIds.some((id) => id === NO_EMAIL)) ||
      isEmpty(email.address)
    );
  }
);

export const getHasInvalidEmailAddress = createSelector(
  getFilteredContactMethodMaintenance,
  (contactMethodMaintenance) => {
    const { verificationIds = [] } = contactMethodMaintenance;
    return (
      verificationIds !== null &&
      verificationIds.some((id) => id === EMAIL_NOT_DELIVERABLE)
    );
  }
);

export const getHasInvalidOrNoEmailAddress = createSelector(
  getHasNoEmailAddress,
  getHasInvalidEmailAddress,
  (noEmailAddress, invalidEmailAddress) =>
    !!(noEmailAddress || invalidEmailAddress)
);

export const getHasInvalidMailingAddressesOnly = createSelector(
  getFilteredContactMethodMaintenance,
  (contactMethodMaintenance) => {
    const { verificationIds = [] } = contactMethodMaintenance;
    return (
      verificationIds !== null &&
      verificationIds.length === INVALID_MAILING_ADDRESSES_ONLY.length &&
      verificationIds.every((id) => INVALID_MAILING_ADDRESSES_ONLY.includes(id))
    );
  }
);

export const getHasInvalidMailingAndEmailAddresses = createSelector(
  getContactMethodMaintenance,
  (contactMethodMaintenance) => {
    const { verificationIds = [] } = contactMethodMaintenance;
    return (
      verificationIds !== null &&
      verificationIds.length === INVALID_MAILING_AND_EMAIL_ADDRESSES.length &&
      verificationIds.every((id) =>
        INVALID_MAILING_AND_EMAIL_ADDRESSES.includes(id)
      )
    );
  }
);

export const getHasInvalidMailingAndNoEmailAddresses = createSelector(
  getContactMethodMaintenance,
  (contactMethodMaintenance) => {
    const { verificationIds = [] } = contactMethodMaintenance;
    return (
      verificationIds !== null &&
      verificationIds.length ===
        INVALID_MAILING_AND_NO_EMAIL_ADDRESSES.length &&
      verificationIds.every((id) =>
        INVALID_MAILING_AND_NO_EMAIL_ADDRESSES.includes(id)
      )
    );
  }
);

export const isAnnualVerificationInvalidMailing = createSelector(
  getContactMethodMaintenance,
  (contactMethodMaintenance) => {
    const { verificationIds = [] } = contactMethodMaintenance;
    return (
      verificationIds !== null &&
      verificationIds.length ===
        VERIFICATION_INVALID_MAILING_ADDRESSES.length &&
      verificationIds.every((id) =>
        VERIFICATION_INVALID_MAILING_ADDRESSES.includes(id)
      )
    );
  }
);

export const isAnnualVerificationInvalidEmail = createSelector(
  getContactMethodMaintenance,
  (contactMethodMaintenance) => {
    const { verificationIds = [] } = contactMethodMaintenance;
    return (
      verificationIds !== null &&
      verificationIds.length === VERIFICATION_INVALID_EMAIL_ADDRESSES.length &&
      verificationIds.every((id) =>
        VERIFICATION_INVALID_EMAIL_ADDRESSES.includes(id)
      )
    );
  }
);

export const isAnnualVerificationNoEmail = createSelector(
  getContactMethodMaintenance,
  (contactMethodMaintenance) => {
    const { verificationIds = [] } = contactMethodMaintenance;
    return (
      verificationIds !== null &&
      verificationIds.length === VERIFICATION_NO_EMAIL_ADDRESSES.length &&
      verificationIds.every((id) =>
        VERIFICATION_NO_EMAIL_ADDRESSES.includes(id)
      )
    );
  }
);

export const isAnnualVerificationInvalidMailingandEmail = createSelector(
  getContactMethodMaintenance,
  (contactMethodMaintenance) => {
    const { verificationIds = [] } = contactMethodMaintenance;
    return (
      verificationIds !== null &&
      verificationIds.length ===
        VERIFICATION_INVALID_MAILING_AND_EMAIL_ADDRESSES.length &&
      verificationIds.every((id) =>
        VERIFICATION_INVALID_MAILING_AND_EMAIL_ADDRESSES.includes(id)
      )
    );
  }
);

export const isAnnualVerificationInvalidMailingandNoEmail = createSelector(
  getContactMethodMaintenance,
  (contactMethodMaintenance) => {
    const { verificationIds = [] } = contactMethodMaintenance;
    return (
      verificationIds !== null &&
      verificationIds.length ===
        VERIFICATION_INVALID_MAILING_AND_NO_EMAIL_ADDRESSES.length &&
      verificationIds.every((id) =>
        VERIFICATION_INVALID_MAILING_AND_NO_EMAIL_ADDRESSES.includes(id)
      )
    );
  }
);

export const getContactMethodMaintenanceDisplayObject = createSelector(
  getHasAnnualVerification,
  getHasInvalidEmailAddressesOnly,
  getHasInvalidMailingAddressesOnly,
  getHasInvalidMailingAndEmailAddresses,
  getHasInvalidMailingAndNoEmailAddresses,
  getHasNoEmailAddressesOnly,
  isAnnualVerificationInvalidMailing,
  isAnnualVerificationInvalidEmail,
  isAnnualVerificationNoEmail,
  isAnnualVerificationInvalidMailingandEmail,
  isAnnualVerificationInvalidMailingandNoEmail,
  (
    hasAnnualVerification,
    hasInvalidEmailAddresses,
    hasInvalidMailingAddresses,
    hasInvalidMailingAndEmailAddresses,
    hasInvalidMailingAndNoEmailAddresses,
    hasNoEmailAddresses,
    hasVerificationInvalidMailing,
    hasVerificationInvalidEmail,
    hasVerificationNoEmail,
    hasVerificationInvalidMailingAndEmail,
    hasVerificationInvalidMailingAndNoEmail
  ) => ({
    hasAnnualVerification,
    hasInvalidEmailAddresses,
    hasInvalidMailingAddresses,
    hasInvalidMailingAndEmailAddresses,
    hasInvalidMailingAndNoEmailAddresses,
    hasNoEmailAddresses,
    errors: {
      hasVerificationInvalidMailing,
      hasVerificationInvalidEmail,
      hasVerificationNoEmail,
      hasVerificationInvalidMailingAndEmail,
      hasVerificationInvalidMailingAndNoEmail
    }
  })
);

export const getHasErrorsForContactMethodMaintenanceModal = createSelector(
  getContactMethodMaintenanceDisplayObject,
  (displayObject) => {
    const { errors } = displayObject;
    return Object.values(errors).some((value) => value);
  }
);

export const getBaseModalTitle = createSelector(
  getContactMethodMaintenanceDisplayObject,
  (displayObject) => {
    if (isContactMethodAnnualVerificationEnabled() && displayObject.hasAnnualVerification) {
      return ANNUAL_VERIFICATION_TITLE;
    }
    if (
      displayObject.hasNoEmailAddresses ||
      displayObject.hasInvalidEmailAddresses
    ) {
      return EMAIL_ADDRESSES_TITLE;
    }
    if (displayObject.hasInvalidMailingAddresses) {
      return MAILING_ADDRESSES_TITLE;
    }
    if (
      displayObject.hasInvalidMailingAndEmailAddresses ||
      displayObject.hasInvalidMailingAndNoEmailAddresses
    ) {
      return CONTACT_INFORMATION_UPDATE_TITLE;
    }
    return '';
  }
);
/**
 *
 * @type {*}
 * Background: This method was added for when a client performs a contact information update in the same session as trying
 * to cancel a pending trade. Because CMM state is intentionally cleared on success fo that update, email address was
 * no longer available on the Activity screen to send to BEP for the confirmation email. There is also an edge case
 * here that would also result in a missing email if CMM data is in a bad state where a primary contact method is not available in the data
 * this would cause an empty email to be returned and sent BEP (should be resolved in data)
 *
 * Longer term approach will be to move this to an isolated selector that reads the IDM email displayed on the Profile - Contact Information Page
 *
 */
export const shouldRefreshContactMethodInformation = createSelector(
  getHasReceivedCMMVerification,
  getCMMEmailAddress,
  (calledCMM, cmmEmail) => !calledCMM || !cmmEmail
);
